import { ChangeEventHandler,  FC, useState, forwardRef, ReactNode } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

interface IInput {
  className?: string;
  type?: string;
  placeholder?: string;
  value?:string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  error?: ReactNode;
  readOnly?: boolean;
}

/**
 * Input component for rendering a styled text input field.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} [props.className] - Additional CSS classes to customize the input's appearance.
 * @param {string} [props.placeholder] - Placeholder text to be displayed inside the input field.
 * @returns {JSX.Element} A styled text input field.
 */

export const Input = forwardRef<HTMLInputElement, IInput>(
  ({ className, placeholder, value, type = 'text', onChange, error, readOnly, ...props }, ref) => {
    return (
      <div className="w-full">
        <input
          ref={ref}
          className={`w-[383px] font-jostMedium text-xl rounded-xl py-3 px-2 placeholder:text-[#1F2B40CC/80] placeholder:font-jostRegular ${
            className ? className : ''} ${error ? 'border-2 border-[rgba(195,32,37,1)]' : 'border-1 border-[#1F2B40]'} ${readOnly ? 'cursor-default focus:outline-none' : ''}`}
          placeholder={placeholder}
          value={value}
          type={type}
          readOnly={readOnly}
          onChange={onChange}
          {...props}
        />
        {error && <span className={'text-[rgba(195,32,37,1)] text-lg leading-[24px]'}>{error}</span>}
      </div>
    );
  }
);

export const InputEmail: FC<IInput> = (
  ({ className, placeholder, value, onChange, error, readOnly, }) => {
    return (
      <div className="flex flex-col items-center gap-y-2">
        <input
          className={`w-[383px] font-jostMedium text-xl rounded-xl py-3 px-2 placeholder:text-[#1F2B40CC/80] placeholder:font-jostRegular ${
            className ? className : ''} ${error ? 'border-2 border-[rgba(195,32,37,1)]' : 'border-1 border-[#1F2B40]'} ${readOnly ? 'cursor-default focus:outline-none' : ''}`}
          type='email'
          placeholder={placeholder}
          value={value}
          readOnly={readOnly}
          onChange={onChange}
        />
        {error && <span className={'text-[rgba(195,32,37,1)] text-lg leading-[24px]'}>{error}</span>}
      </div>
    );
  }
);

/**
 * InputPassword component for rendering a styled password input field
 * with a toggle button to show or hide the password.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {string} [props.className] - Additional CSS classes to customize the input's appearance.
 * @param {string} [props.placeholder] - Placeholder text to be displayed inside the input field.
 * @returns {JSX.Element} A styled password input field with visibility toggle functionality.
 */

export const InputPassword: FC<IInput> = ({
  className,
  placeholder,
  value,
  onChange,
  error
}): JSX.Element => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className={'flex flex-col items-center gap-y-2'}>
      <div className="relative w-[383px]">
        <input
          type={showPassword ? 'text' : 'password'}
          className={`border-1 w-full border-[#1F2B40] font-jostMedium text-xl rounded-xl py-3 px-2 placeholder:text-[#1F2B40CC/80] placeholder:font-jostRegular ${
            className ? className : ''} ${error ? 'border-2 border-[rgba(195,32,37,1)]' : 'border-1 border-[#1F2B40]'}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none">
          {showPassword ? (
            <EyeSlashIcon className="size-6 text-[#1F2B40CC/80] cursor-pointer" />
          ) : (
            <EyeIcon className="size-6 text-[#1F2B40CC/80] cursor-pointer" />
          )}
        </button>
      </div>
      {error && <span className={'text-[rgba(195,32,37,1)] text-lg leading-[24px]'}>{error}</span>}
    </div>
  );
};
